/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ChangeDetectorRef,
	Component,
	HostBinding,
	HostListener,
	OnDestroy,
	OnInit
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormArray
} from '@angular/forms';
import {
	FieldArrayType,
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	FormlyFieldConfigCache,
	FormlyValueChangeEvent
} from '@ngx-formly/core/lib/models';
import {
	AppEventParameterConstants
} from '@shared/constants/app-event-parameter.constants';
import {
	AppEventConstants
} from '@shared/constants/app-event.constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	WindowEventConstants
} from '@shared/constants/window-event.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	FormlyHelper
} from '@shared/helpers/formly.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IKeyValuePair
} from '@shared/interfaces/application-objects/key-value-pair.interface';
import {
	IObjectSearch
} from '@shared/interfaces/application-objects/object-search.interface';
import {
	cloneDeep,
	each,
	isArray,
	isObject,
	unset
} from 'lodash-es';
import {
	DateTime
} from 'luxon';
import {
	Subject,
	Subscription,
	concatAll,
	debounceTime,
	from,
	interval,
	map,
	take
} from 'rxjs';

@Component({
	selector: 'custom-repeater',
	templateUrl: './custom-repeater.component.html',
	styleUrls: ['./custom-repeater.component.scss']
})

/**
 * A component representing an instance of a Custom Repeater.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomRepeaterComponent
 * @extends {FieldArrayType}
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
export class CustomRepeaterComponent
	extends FieldArrayType
	implements OnInit, OnDestroy
{
	/**
	 * Initializes a new instance of the CustomRepeaterComponent. This
	 * explicit constructor was requested during an upgrade process and
	 * should be left as implemented.
	 *
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof CustomRepeaterComponent
	 */
	public constructor(
		public changeDetector: ChangeDetectorRef)
	{
		super();
	}

	/**
	 * Gets or sets the value used to define if the add
	 * item is visible in the section title.
	 *
	 * @type {boolean}
	 * @memberof CustomRepeaterComponent
	 */
	public addItemVisible: boolean = true;

	/**
	 * Gets or sets the value used to define if the repeater is currently
	 * handling a display all event. This is used to disable the add item
	 * if applicable until all are displayed.
	 *
	 * @type {boolean}
	 * @memberof CustomRepeaterComponent
	 */
	public displayAllInProgress: boolean = false;

	/**
	 * Gets or sets the value used to define if the virtual
	 * page display is enabled for the repeater.
	 *
	 * @type {boolean}
	 * @memberof CustomRepeaterComponent
	 */
	public virtualPageDisplay: boolean = false;

	/**
	 * Gets or sets the array change observer subject which is called when an
	 * item is added or removed.
	 *
	 * @type {Subject<void>}
	 * @memberof CustomRepeaterComponent
	 */
	public arrayChangeObserver:
		Subject<void> = new Subject<void>();

	/**
	 * Gets or sets the scroll observer subject which is called by a window
	 * scroll event.
	 *
	 * @type {Subject<void>}
	 * @memberof CustomRepeaterComponent
	 */
	public scrollObserver:
		Subject<void> = new Subject<void>();

	/**
	 * Gets or sets the object search to use for limited list displays.
	 *
	 * @type {IObjectSearch}
	 * @memberof CustomRepeaterComponent
	 */
	public objectSearch: IObjectSearch =
		<IObjectSearch>
		{
		};

	/**
	 * Gets the number of items required to enforce change detection on scroll.
	 * This is used to fire change detection on the the repeater when a large
	 * number of items exist and the user scrolls faster than the UI is able
	 * to update.
	 *
	 * @type {number}
	 * @memberof CustomRepeaterComponent
	 */
	private readonly scrollChangeDetectionCount: number = 25;

	/**
	 * Gets or sets the subscription used to listen for form value changes
	 * and trigger change detection for reactive title updates.
	 *
	 * @type {Subscription}
	 * @memberof CustomRepeaterComponent
	 */
	private titleChangeSubscription: Subscription;

	/**
	 * Handles the scroll event sent from the window.
	 * This will fire the change detector to redraw for large repeaters.
	 *
	 * @memberof CustomFieldWrapperComponent
	 */
	@HostListener(
		WindowEventConstants.scrollEvent)
	public scroll(): void
	{
		if (this.field.fieldGroup.length > this.scrollChangeDetectionCount)
		{
			this.scrollObserver.next();
		}
	}

	/**
	 * Exposes the repeater's display type string as a DOM data attribute
	 * so that rule navigation can identify the specific typed-asset repeater
	 * to scroll to when multiple repeaters share the same data key.
	 *
	 * @returns {string | null}
	 * The filter function string bound to this repeater, or null when no
	 * filter is configured.
	 * @memberof CustomRepeaterComponent
	 */
	@HostBinding(
		`attr.${FormlyConstants.attributeKeys.dataRepeaterType}`)
	public get dataRepeaterType(): string | null
	{
		const applicableTypes: string[] | null =
			this.field?.props?.applicableTypes ?? null;

		return !AnyHelper.isNull(applicableTypes)
			&& applicableTypes.length > 0
				? applicableTypes.join(' ')
				: null;
	}

	/**
	 * Handles a shared repeater item event by ensuring that any shared
	 * repeaters are updated to hold the same values as the altered repeater.
	 *
	 * @param {string} repeaterKey
	 * The repeater's data location key.
	 * @param {string} repeaterFilter
	 * The repeaters data filter for shared repeaters.
	 * @param {string} repeaterAction
	 * The add or remove repeater action.
	 * @param {number} itemIndex
	 * The item index added/removed to/from the repeater.
	 * @memberof CustomRepeaterComponent
	 */
	@HostListener(
		AppEventConstants.repeaterItemAlteredEvent,
		[
			AppEventParameterConstants.repeaterKey,
			AppEventParameterConstants.repeaterFilter,
			AppEventParameterConstants.repeaterAction,
			AppEventParameterConstants.itemIndex
		])
	public repeaterItemAltered(
		repeaterKey: string,
		repeaterFilter: string,
		repeaterAction: string,
		itemIndex: number = null): void
	{
		if (this.field.key !== repeaterKey)
		{
			return;
		}

		if (AnyHelper.isNull(this.field.props.filter)
			|| this.field.props.filter === repeaterFilter)
		{
			if (!AnyHelper.isNull(itemIndex)
				&& !AnyHelper.isNull(this.model))
			{
				// Cleaning the field item from model.
				this.cleanModelField(
					this.model[itemIndex],
					itemIndex,
					(<any>this.field.fieldArray)?.fieldGroup);
			}

			// Cleaning existing field items from model.
			this.cleanExistingModelFields(this.field);

			this.InitializeFormControls();

			return;
		}

		if (repeaterAction === FormlyConstants.repeaterActions.add)
		{
			this.options.build(this.field.parent);
			this.options.fieldChanges.next(
				<FormlyValueChangeEvent>
				{
					field: this.field.parent,
					value: {...this.field.parent.formControl.value},
					type: FormlyConstants.valueChangeTypes.valueChanges
				});
		}
		else
		{
			this.removeFromArray(
				itemIndex,
				false);
		}

		this.updateNestedRepeaterContext(this.field);
		this.validateControl();

		// Cleaning existing field items from model.
		this.cleanExistingModelFields(this.field);
	}

	/**
	 * This will sync up the model for all existing repeater fields.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	@HostListener(
		AppEventConstants.repeaterCleanModelEvent)
	public repeaterModelSync(): void
	{
		this.cleanExistingModelFields(this.field);
	}

	/**
	 * Handles the on initialization event.
	 * This method will setup minimum and max length validations depending
	 * on the sent template options.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	public ngOnInit(): void
	{
		const canCreate: boolean =
			this.field
				.props
				?.securityAccessPolicy
				?.rights
				?.create
				?? false;

		this.addItemVisible =
			!this.field.props.disabled
				&& canCreate;

		this.field.props.removeAddItem =
			this.removeAddItem.bind(this);
		this.field.props.addItem =
			this.addItem.bind(this);
		this.field.props.removeItem =
			this.removeItem.bind(this);

		this.updateNestedRepeaterContext(
			this.field);

		if (this.hasRootModelPath())
		{
			this.bindRootModel();
		}

		// Run an overall validation check after adds of multiple items.
		this.arrayChangeObserver.pipe(
			debounceTime(AppConstants.time.quarterSecond))
			.subscribe(
				() =>
				{
					this.addItemVisible =
						!this.field.props.disabled
							&& canCreate;

					FormlyHelper.fireViewCheck(
						this.field.fieldGroup);

					this.fireChanges();
				});

		this.scrollObserver.pipe(
			debounceTime(AppConstants.time.halfSecond))
			.subscribe(
				() =>
				{
					this.fireChanges();
				});

		if (!AnyHelper.isNull(this.field.formControl?.valueChanges))
		{
			this.titleChangeSubscription =
				this.field.formControl.valueChanges.pipe(
					debounceTime(AppConstants.time.quarterSecond))
					.subscribe(
						() =>
						{
							this.changeDetector.detectChanges();
						});
		}

		this.objectSearch.virtualPageSize =
			this.field.props?.virtualPageSize;
		let remainingVirtualItems: number =
			this.objectSearch.virtualPageSize;
		this.virtualPageDisplay =
			!AnyHelper.isNullOrWhitespace(
				this.objectSearch.virtualPageSize);

		this.field.fieldGroup.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				fieldGroup.props.display =
					!AnyHelper.isNullOrWhitespace(
						this.field.props.filter)
						? StringHelper
							.transformToFunction(
								this.field.props.filter,
								this.field.props.context)(fieldGroup)
						: true;

				fieldGroup.props.virtualDisplay =
					fieldGroup.props.display === true
						&& (this.virtualPageDisplay === false
							|| remainingVirtualItems > 0);

				if (fieldGroup.props.virtualDisplay === true)
				{
					remainingVirtualItems--;
				}
			});

		this.virtualPageDisplay =
			this.virtualPageDisplay === true
				&& this.field.fieldGroup.filter(
					(field: FormlyFieldConfig) =>
						field.props.display === true)
					.length > this.objectSearch.virtualPageSize;

		this.cleanExistingModelFields(this.field);

		if (!AnyHelper.isNullOrEmpty(
			this.field.props.minimumNumber)
			|| !AnyHelper.isNullOrEmpty(
				this.field.props.maximumNumber))
		{
			this.validateControl();

			return;
		}

		if (!(this.field.props.minItems > 0
			|| this.field.props.maxItems > 0))
		{
			this.validateControl();

			return;
		}

		this.setRepeaterCountValidity();
		this.validateControl();
	}

	/**
	 * Handles the display all event.
	 * This will capture display all events and set all existing displayed
	 * items to be displayed in the current view.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	public displayAll(): void
	{
		setTimeout(
			() =>
			{
				this.virtualPageDisplay = false;
				this.displayAllInProgress = true;
			});

		this.field.fieldGroup.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				setTimeout(
					() =>
					{
						fieldGroup.props.virtualDisplay =
							fieldGroup.props.display === true;

						this.fireChanges();
					});
			});

		setTimeout(
			() =>
			{
				this.displayAllInProgress = false;
			});
	}

	/**
	 * Removes the add item button from the section title.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	public removeAddItem(): void
	{
		this.addItemVisible = false;
	}

	/**
	 * On destroy event.
	 * This will complete and observables or subscriptions on this component.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	public ngOnDestroy(): void
	{
		this.arrayChangeObserver.complete();
		this.scrollObserver.complete();
		this.titleChangeSubscription?.unsubscribe();
	}

	/**
	 * Reaches into the field currently being displayed and sets the
	 * context value for each nested fieldgroup.
	 *
	 * @param {FormlyFieldConfig} field
	 * The field value used to set nested repeater context values.
	 * @memberof CustomRepeaterComponent
	 */
	public updateNestedRepeaterContext(
		field: FormlyFieldConfig): void
	{
		// Make sure new controls are not linked by name as we want
		// each field to validate standalone.
		if (!AnyHelper.isNull((<any>field.formControl)?._fields))
		{
			delete (<any>field.formControl)._fields;
		}

		if (!AnyHelper.isNull((<any>field.formControl)?._controls)
			&& isArray((<any>field.formControl)?._controls))
		{
			const controls = (<any>field.formControl)?._controls;
			if (!AnyHelper.isNull(controls))
			{
				for (const nestedField of controls)
				{
					if (!AnyHelper.isNull(nestedField.formControl?._fields))
					{
						delete nestedField.formControl._fields;
					}
				}
			}
		}

		field.fieldGroup?.forEach(
			(fieldGroupItem: FormlyFieldConfig) =>
			{
				fieldGroupItem.props.context =
					this.field.props.context;

				this.updateNestedRepeaterContext(fieldGroupItem);
			});

		(<any>field.fieldArray)?.fieldGroup.forEach(
			(fieldArrayItem: FormlyFieldConfig) =>
			{
				fieldArrayItem.props.context =
					this.field.props.context;

				this.updateNestedRepeaterContext(fieldArrayItem);
			});
	}

	/**
	 * Handles the on tab expand event sent from the accordion component.
	 * This will fire a view check on the expanded item to ensure the latest
	 * business rules are applied.
	 *
	 * @param {number} itemIndex
	 * The item index that received the expand event.
	 * @memberof CustomRepeaterComponent
	 */
	public onTabExpand(
		itemIndex: number): void
	{
		this.updateViewValidations(itemIndex);
	}

	/**
	 * A decoration method to the formly add function allowing
	 * this component to mark the form as dirty on adding an
	 * array data item.
	 *
	 * @param {any} initialModel
	 * If sent, the new item will be created with
	 * this object as its initial model.
	 * @memberof CustomRepeaterComponent
	 */
	public addItem(
		initialModel?: any): void
	{
		if (this.hasRootModelPath()
			&& AnyHelper.isNullOrWhitespace(
				this.getParentResourceIdentifier()))
		{
			return;
		}

		this.addToArray(
			null,
			initialModel
				?? this.field.props.initialItemValue);

		const newItemIndex: number = this.field.fieldGroup.length - 1;
		this.updateNestedRepeaterContext(this.field);
		this.field.fieldGroup[newItemIndex]
			.props.display = true;
		this.field.fieldGroup[newItemIndex]
			.props.virtualDisplay = true;

		this.updateNestedRepeaterContext(
			this.field.fieldGroup[newItemIndex]);
		this.validateControl();

		this.updateViewValidations(newItemIndex);
	}

	/**
	 * A decoration method to the formly add function allowing
	 * this component to update all listening shared repeaters.
	 *
	 * @param {number} itemIndex
	 * If sent this will add the item to the sent index, this value defaults
	 * to the last item in the array.
	 * @param {any} initialModel
	 * If sent this will create the new item with this sent object as it's
	 * data structure. This value will default to an empty object.
	 * @memberof CustomRepeaterComponent
	 */
	public addToArray(
		itemIndex?: number,
		initialModel?: any)
	{
		const newItemIndex: number =
			AnyHelper.isNullOrEmpty(itemIndex)
				? this.model?.length ?? 0
				: itemIndex;

		if (this.hasRootModelPath())
		{
			this.addToRootArray(
				newItemIndex,
				initialModel);

			return;
		}

		if (AnyHelper.isNull(this.model))
		{
			this.field.formControl.setValue([]);
		}

		const newItem: any =
			initialModel
				? cloneDeep(initialModel)
				: undefined;

		this.model.splice(
			newItemIndex,
			0,
			newItem);

		this.finalizeAdd(
			newItemIndex,
			newItem);
	}

	/**
	 * A decoration method to the formly delete function allowing
	 * this component to mark the form as dirty on deleting an
	 * array data item.
	 *
	 * @param {FormlyFieldConfig} fieldToRemove
	 * The item being deleted.
	 * @param {Event} event
	 * The remove item event.
	 * @memberof CustomRepeaterComponent
	 */
	public removeItem(
		fieldToRemove: FormlyFieldConfig,
		event: Event): void
	{
		const itemIndex: number =
			this.field.fieldGroup.indexOf(fieldToRemove);

		if (this.hasRootModelPath())
		{
			this.removeFromRootArray(itemIndex, event);

			return;
		}

		if (this.findRootBoundChildRepeaters(fieldToRemove).length > 0)
		{
			this.removeWithChildren(
				fieldToRemove,
				itemIndex,
				event);

			return;
		}

		this.removeFromArray(itemIndex);
		this.updateNestedRepeaterContext(this.field);
		this.validateControl(event);

		EventHelper.dispatchRepeaterItemAlteredEvent(
			this.field.key.toString(),
			this.field.props.filter,
			FormlyConstants.repeaterActions.remove,
			itemIndex);
	}

	/**
	 * Returns the validity of the current repeater if a maximum item
	 * or minimum item property exists or true if not defined.
	 *
	 * @returns {boolean}
	 * The current validity of the repeater length.
	 * @memberof CustomRepeaterComponent
	 */
	public hasAValidRepeaterCount(
		control: AbstractControl): boolean
	{
		const minItems: number =
			this.field.props.minItems;
		const maxItems: number =
			this.field.props.maxItems;

		return (AnyHelper.isNull(minItems)
			? true
			: control.value.length >= minItems)
			&& (AnyHelper.isNull(maxItems)
				? true
				: control.value.length <= maxItems);
	}

	/**
	 * Returns the disabled value of the current repeater item as true if all
	 * fields in the item are disabled. This is used to hide or show the
	 * delete action on the item.
	 *
	 * @param {FormlyFieldConfig} customField
	 * The field configuration for the repeater item.
	 * @returns {boolean}
	 * The current disabled value of the repeater item.
	 * @memberof CustomRepeaterComponent
	 */
	public isDisabledItem(
		customField: FormlyFieldConfig): boolean
	{
		return customField.fieldGroup.filter(
			(item: FormlyFieldConfig) =>
				item.key !== AppConstants.commonProperties.resourceIdentifier
					&& item.props.disabled !== true)
			.length === 0;
	}

	/**
	 * Gets a custom title to be used for displaying this repeater item.
	 * This can be customized via the props.titleFormat value in
	 * the entity layout. This performs similar to string interpolation
	 * except only if values exist for all requested properties will the
	 * titleFormat result be returned.
	 * @summary
	 * A title format of '{city}, {state}' will find the controls in this
	 * repeater item with these matching keys and replace the bracketed
	 * property names with their value. This will then prepend the singular
	 * item type as well as the item index as a word.
	 * ie. Location One: Butte, MT.
	 *
	 * @param {number} itemIndex
	 * The item index for which the header is being generated for.
	 * @param {FormlyFieldConfig} currentField
	 * The current field group for which the header is being generated for.
	 * @returns {string}
	 * The title with values parsed from the object matching the title
	 * format of this repeater type.
	 * @memberof CustomRepeaterComponent
	 */
	public getCustomTitle(
		currentField: FormlyFieldConfig): string
	{
		if (AnyHelper.isNullOrEmpty(this.field))
		{
			return AppConstants.empty;
		}

		let customFormattedTitle: string =
			AppConstants.empty;

		const customFieldGroup: FormlyFieldConfig[] =
			this.field.fieldGroup.filter(
				(field: FormlyFieldConfig) =>
					field.props.virtualDisplay === true);

		const itemIndex: number =
			customFieldGroup.indexOf(currentField);

		const indexNumberAsWord: string =
			StringHelper.numberToWords(
				(itemIndex + 1).toString(),
				true);
		const itemIdentifier: string =
			this.field.props.singular
				+ ' '
				+ indexNumberAsWord;

		if (!AnyHelper.isNullOrEmpty(
			this.field.props.titleFunction))
		{
			const functionTitle: string =
				StringHelper.transformToFunction(
					StringHelper.interpolate(
						this.field.props.titleFunction,
						this.field.props.context),
					this.field.props.context)(currentField);

			if (this.field.props.hideSingularIdentifier === true)
			{
				return functionTitle;
			}

			customFormattedTitle =
				AnyHelper.isNullOrWhitespace(functionTitle)
					? itemIdentifier
					: `${itemIdentifier}: ${functionTitle}`;

			return customFormattedTitle;
		}

		if (AnyHelper.isNullOrEmpty(
			this.field.props.titleFormat))
		{
			return itemIdentifier;
		}

		const words: IKeyValuePair[] =
			this.getCurrentItemTitleValues(
				currentField,
				this.field.props.titleFormat);

		const isPartialTitleMatch: boolean =
			words.some((keyValuePair: IKeyValuePair) =>
				!AnyHelper.isNullOrWhitespace(keyValuePair.value));

		if (isPartialTitleMatch === false)
		{
			return itemIdentifier;
		}

		let formattedTitle: string =
			this.field.props.titleFormat;

		words.forEach(
			(keyValuePair: IKeyValuePair) =>
			{
				if (AnyHelper.isNullOrWhitespace(keyValuePair.value))
				{
					formattedTitle = formattedTitle.replace(
						keyValuePair.key,
						AppConstants.empty);
				}
				else
				{
					const fieldKey: string =
						StringHelper
							.getCleanedValue(
								keyValuePair.key,
								['{', '}']);

					const formlyField: FormlyFieldConfig =
						currentField.get(fieldKey);

					if (formlyField
						&& formlyField.type
							=== FormlyConstants.customControls.customSelect)
					{
						const options: IDropdownOption[] =
							<IDropdownOption[]>
							formlyField.props.options;

						const option: IDropdownOption =
							options.find((dropdownOption: IDropdownOption) =>
								dropdownOption.value === keyValuePair.value);

						formattedTitle = formattedTitle.replace(
							keyValuePair.key,
							option?.label ?? keyValuePair.value);
					}
					else
					{
						formattedTitle = formattedTitle.replace(
							keyValuePair.key,
							keyValuePair.value);
					}
				}
			});

		formattedTitle =
			formattedTitle
				.replace(
					/(^\s-\s|^\s*|\s-\s+$|\s+$)/g,
					AppConstants.empty);

		if (this.field.props.hideSingularIdentifier === true)
		{
			return formattedTitle;
		}

		customFormattedTitle =
			AnyHelper.isNullOrWhitespace(formattedTitle)
				? itemIdentifier
				: `${itemIdentifier}: ${formattedTitle}`;

		return customFormattedTitle;
	}

	/**
	 * Given a current item in the repeater set, this will return
	 * the cleaned set of keys and values that match the title format.
	 * @summary
	 * A title format of '{city}, {state}' will find the controls in this
	 * repeater item with these matching keys and return an array of
	 * key value pairs with a key of {city}, and a value of the form controls
	 * value in the UI. If the value is an empty string or null, null is
	 * returned.
	 *
	 * @param {FormlyFieldConfig} currentItem
	 * The current repeater item.
	 * @param {string} titleFormat
	 * The title format being used to return the set of associated key value
	 * pairs of data for this item's custom title.
	 * @returns {IKeyValuePair[]}
	 * The set of key value pairs with the key word and value matching the title
	 * format word tokens.
	 * @memberof CustomRepeaterComponent
	 */
	private getCurrentItemTitleValues(
		currentItem: FormlyFieldConfig,
		titleFormat: string): IKeyValuePair[]
	{
		const wordTokens: RegExpMatchArray =
			titleFormat.match(/{[\w.\d]+}/g);
		const words: IKeyValuePair[] = [];

		wordTokens.forEach(
			(wordToken: string) =>
			{
				const titleControl: FormlyFieldConfig[] =
					currentItem.fieldGroup
						.filter(
							(item: FormlyFieldConfig) =>
								AnyHelper.isNullOrWhitespace(item.key)
									? AppConstants.empty
									: (item.key.toString())
										.startsWith(
											StringHelper
												.getCleanedValue(
													wordToken,
													['{', '}'])) === true);

				const titleControlValue: string =
					!AnyHelper.isNullOrEmpty(titleControl)
						&& titleControl.length > 0
						? titleControl[0].formControl.value
						: null;

				let wordValue: string = titleControlValue;
				if (DateTime.fromISO(titleControlValue).isValid === true
					&& titleControl[0].type ===
						FormlyConstants.customControls.customCalendar)
				{
					wordValue =
						DateTime
							.fromISO(
								titleControlValue)
							.toFormat(
								DateHelper.presetFormats.longDateFormat);
				}
				else if (!AnyHelper.isNullOrWhitespace(titleControlValue)
					&& titleControl[0].type ===
						FormlyConstants.customControls.customInputNumber
					&& titleControl[0].props.useCurrency === true)
				{
					wordValue =
						StringHelper.format(
							titleControlValue,
							AppConstants.formatTypes.currency);
				}

				words.unshift(
					<IKeyValuePair>
					{
						key: wordToken,
						value: !AnyHelper.isNullOrEmpty(wordValue)
							? wordValue
							: null
					});
			});

		return words;
	}

	/**
	 * Attaches a minimum and maximum-length validator to the repeater control
	 * based on the sent minItems and maxItems properties.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private setRepeaterCountValidity(): void
	{
		this.field.validators = this.field.validators || {};

		const minItems: number =
			this.field.props.minItems;
		const maxItems: number =
			this.field.props.maxItems;
		const minItemMessage: string = minItems > 1
			? 'items are'
			: 'item is';
		const maxItemMessage: string = maxItems > 1
			? 'items are'
			: 'item is';

		this.field.validators.arrayLength = {
			expression: (control: AbstractControl) =>
				this.hasAValidRepeaterCount(control),
			message: AnyHelper.isNull(maxItems)
				? `At least ${minItems} ${minItemMessage} required.`
				: `Between ${minItems || 0} `
					+ `and ${maxItems} ${maxItemMessage} required.`
		};

		this.validateControl();
	}

	/**
	 * Validates the existing control with a set as touched and a call to update
	 * value and validity.
	 *
	 * @param {Event} event
	 * The validate control item event.
	 * @memberof CustomRepeaterComponent
	 */
	private validateControl(event?: Event): void
	{
		this.field.formControl.markAsDirty();
		this.field.formControl.markAllAsTouched();
		this.field.formControl.updateValueAndValidity();
		this.fireChanges(event);
	}

	/**
	 * Fires a change detection cycle allowing the form to broadcast changes
	 * to the listening component.
	 * @note This can likely be removed with an upgraded Formly as this is
	 * a workaround to the view not aligning to the state of the formcontrol.
	 * This was required starting with Formly v6.0.0-next.1.
	 *
	 * @param {Event} event
	 * The fire changes item event.
	 * @memberof CustomRepeaterComponent
	 */
	private fireChanges(event?: Event): void
	{
		this.changeDetector.detectChanges();

		if (AnyHelper.isFunction(this.field.props.change))
		{
			this.field.props.change(
				this.field,
				event);
		}
	}

	/**
	 * Returns true when this repeater is configured with a root model path,
	 * indicating it reads and writes through the shared root array.
	 *
	 * @returns {boolean}
	 * True when rootModelPath is set on this field's props.
	 * @memberof CustomRepeaterComponent
	 */
	private hasRootModelPath(): boolean
	{
		return !AnyHelper.isNullOrWhitespace(
			this.field.props.rootModelPath as string);
	}

	/**
	 * Rebuilds the parent field and broadcasts the value change event.
	 * Called after any model mutation to keep formly in sync.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private rebuildParent(): void
	{
		this.options.build(this.field.parent);
		this.options.fieldChanges.next(
			<FormlyValueChangeEvent>
			{
				field: this.field.parent,
				value: {...this.field.parent.formControl.value},
				type: FormlyConstants.valueChangeTypes.valueChanges
			});
	}

	/**
	 * Dispatches a repeater item altered event for this field.
	 *
	 * @param {string} action
	 * The repeater action (add or remove).
	 * @param {number} itemIndex
	 * The item index that was added or removed.
	 * @memberof CustomRepeaterComponent
	 */
	private dispatchRepeaterAltered(
		action: string,
		itemIndex: number): void
	{
		EventHelper.dispatchRepeaterItemAlteredEvent(
			this.field.key.toString(),
			this.field.props.filter,
			action,
			itemIndex);
	}

	/**
	 * Finalizes an add operation by rebuilding the parent, aligning
	 * initial item values, and dispatching the add event.
	 *
	 * @param {number} itemIndex
	 * The index of the newly added item.
	 * @param {any} model
	 * The initial model for the new item.
	 * @memberof CustomRepeaterComponent
	 */
	private finalizeAdd(
		itemIndex: number,
		model: any): void
	{
		this.rebuildParent();
		this.alignInitialItemValues(
			itemIndex,
			model);
		this.dispatchRepeaterAltered(
			FormlyConstants.repeaterActions.add,
			itemIndex);
	}

	/**
	 * Finalizes a remove operation by rebuilding the parent, updating
	 * nested context, validating, and dispatching the remove event.
	 *
	 * @param {number} itemIndex
	 * The index of the removed item.
	 * @param {Event} event
	 * The triggering event.
	 * @memberof CustomRepeaterComponent
	 */
	private finalizeRemove(
		itemIndex: number,
		event: Event): void
	{
		this.rebuildParent();
		this.recalculateVirtualDisplay();
		this.recalculateSiblingRepeaterDisplay();

		this.updateNestedRepeaterContext(this.field);
		this.validateControl(event);
		this.dispatchRepeaterAltered(
			FormlyConstants.repeaterActions.remove,
			itemIndex);
	}

	/**
	 * Recalculates display flags for this repeater after model mutations
	 * that rebuild formly fields and may shift fieldGroup/model alignment.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private recalculateVirtualDisplay(): void
	{
		this.recalculateFieldDisplay(this.field);

		this.virtualPageDisplay =
			this.virtualPageDisplay === true
				&& this.field.fieldGroup.filter(
					(field: FormlyFieldConfig) =>
						field.props.display === true)
					.length > this.objectSearch.virtualPageSize;
	}

	/**
	 * Recalculates display flags on root-bound sibling repeaters under
	 * the same parent because rebuildParent affects the entire subtree.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private recalculateSiblingRepeaterDisplay(): void
	{
		if (AnyHelper.isNull(this.field.parent))
		{
			return;
		}

		const siblingRepeaters: FormlyFieldConfig[] =
			this.findRootBoundChildRepeaters(this.field.parent);

		siblingRepeaters.forEach(
			(siblingRepeater: FormlyFieldConfig) =>
			{
				if (siblingRepeater === this.field)
				{
					return;
				}

				this.recalculateFieldDisplay(siblingRepeater);
			});
	}

	/**
	 * Applies filter-driven display and virtualDisplay flags to the
	 * sent repeater field group.
	 *
	 * @param {FormlyFieldConfig} targetField
	 * The repeater field to recalculate.
	 * @memberof CustomRepeaterComponent
	 */
	private recalculateFieldDisplay(
		targetField: FormlyFieldConfig): void
	{
		if (AnyHelper.isNull(targetField)
			|| AnyHelper.isNull(targetField.fieldGroup))
		{
			return;
		}

		const virtualPageSize: number =
			targetField.props?.virtualPageSize;
		let remainingVirtualItems: number =
			virtualPageSize;
		const hasPageLimit: boolean =
			!AnyHelper.isNullOrWhitespace(virtualPageSize);

		targetField.fieldGroup.forEach(
			(fieldGroup: FormlyFieldConfig) =>
			{
				if (AnyHelper.isNull(fieldGroup))
				{
					return;
				}

				if (AnyHelper.isNull(fieldGroup.props))
				{
					fieldGroup.props = {};
				}

				if (AnyHelper.isNull(fieldGroup.model))
				{
					fieldGroup.props.display = false;
					fieldGroup.props.virtualDisplay = false;

					return;
				}

				fieldGroup.props.display =
					AnyHelper.isNullOrWhitespace(
						targetField.props.filter)
						? true
						: StringHelper
							.transformToFunction(
								targetField.props.filter,
								targetField.props.context)(fieldGroup);

				fieldGroup.props.virtualDisplay =
					fieldGroup.props.display === true
						&& (hasPageLimit === false
							|| remainingVirtualItems > 0);

				if (fieldGroup.props.virtualDisplay === true)
				{
					remainingVirtualItems--;
				}
			});
	}

	/**
	 * Adds a new item to the root array. Stamps the parent reference
	 * identifier onto the item before pushing it, then rebuilds the
	 * form and dispatches the add event.
	 *
	 * @param {number} newItemIndex
	 * The index the new item is being inserted at.
	 * @param {any} initialModel
	 * The initial model value for the new item.
	 * @memberof CustomRepeaterComponent
	 */
	private addToRootArray(
		newItemIndex: number,
		initialModel?: any): void
	{
		const rootArray: any[] =
			this.resolveRootArray();
		const parentReferenceIdKey: string =
			(this.field.props.parentReferenceIdKey as string)
				?? AppConstants.commonProperties.parentReferenceId;
		const newItem: any =
			{
				...cloneDeep(initialModel),
				[parentReferenceIdKey]: this.getParentResourceIdentifier()
			};

		rootArray.push(newItem);

		this.finalizeAdd(
			newItemIndex,
			newItem);
	}

	/**
	 * Removes an item from the root array. Locates the item by object
	 * reference, splices it out, then rebuilds the form and dispatches
	 * the remove event.
	 *
	 * @param {number} itemIndex
	 * The index of the item to remove from the local model array.
	 * @param {Event} event
	 * The triggering event.
	 * @memberof CustomRepeaterComponent
	 */
	private removeFromRootArray(
		itemIndex: number,
		event: Event): void
	{
		const itemToRemove: any =
			this.model[itemIndex];
		const rootArray: any[] =
			this.resolveRootArray();
		const rootIndex: number =
			rootArray.indexOf(itemToRemove);

		if (rootIndex !== -1)
		{
			rootArray.splice(rootIndex, 1);
		}

		this.finalizeRemove(
			itemIndex,
			event);
	}

	/**
	 * Recursively scans the field group tree of a parent item for
	 * nested repeaters whose items live in a shared root array linked
	 * by reference ID. This handles both flat layouts (direct children)
	 * and tab layouts (children nested inside intermediary formly-groups).
	 *
	 * @param {FormlyFieldConfig} parentField
	 * The parent item's field configuration to scan for child repeaters.
	 * @returns {FormlyFieldConfig[]}
	 * The set of child repeater field configurations that use rootModelPath.
	 * @memberof CustomRepeaterComponent
	 */
	private findRootBoundChildRepeaters(
		parentField: FormlyFieldConfig): FormlyFieldConfig[]
	{
		const results: FormlyFieldConfig[] = [];

		for (const child of parentField.fieldGroup ?? [])
		{
			if (!AnyHelper.isNullOrWhitespace(
				child.props?.rootModelPath as string))
			{
				results.push(child);
			}
			else if (child.fieldGroup)
			{
				results.push(
					...this.findRootBoundChildRepeaters(child));
			}
		}

		return results;
	}

	/**
	 * Removes a parent item and all its root-bound children from
	 * the model. Children are identified by matching their reference
	 * ID key to the parent's resource identifier. After all items
	 * are removed, the form is fully rebuilt.
	 *
	 * @param {FormlyFieldConfig} fieldToRemove
	 * The parent field configuration being removed.
	 * @param {number} itemIndex
	 * The original index of the parent in the field group.
	 * @param {Event} event
	 * The triggering event.
	 * @memberof CustomRepeaterComponent
	 */
	private removeWithChildren(
		fieldToRemove: FormlyFieldConfig,
		itemIndex: number,
		event: Event): void
	{
		const parentItem: any = this.model[itemIndex];
		const childRepeaters: FormlyFieldConfig[] =
			this.findRootBoundChildRepeaters(fieldToRemove);
		const indicesToRemove: Set<number> =
			new Set<number>([itemIndex]);

		for (const childRepeater of childRepeaters)
		{
			const parentReferenceIdKey: string =
				(childRepeater.props.parentReferenceIdKey as string)
					?? AppConstants.commonProperties.parentReferenceId;
			const parentReferenceKey: string =
				(childRepeater.props.parentReferenceKey as string)
					?? AppConstants.commonProperties.resourceIdentifier;
			const parentResourceIdentifier: string =
				parentItem[parentReferenceKey];

			if (AnyHelper.isNullOrWhitespace(parentResourceIdentifier))
			{
				continue;
			}

			for (let index = 0; index < this.model.length; index++)
			{
				if (this.model[index][parentReferenceIdKey]
					=== parentResourceIdentifier)
				{
					indicesToRemove.add(index);
				}
			}
		}

		const sortedDescending: number[] =
			[...indicesToRemove].sort(
				(indexA: number, indexB: number) =>
					indexB - indexA);

		for (const removeIndex of sortedDescending)
		{
			this.removeFromArray(
				removeIndex,
				true,
				false);
		}

		this.updateNestedRepeaterContext(this.field);
		this.validateControl(event);
		this.dispatchRepeaterAltered(
			FormlyConstants.repeaterActions.remove,
			itemIndex);
	}

	/**
	 * Walks up to the root formly field and returns the array at
	 * the path defined by rootModelPath.
	 *
	 * @returns {any[]}
	 * The root array.
	 * @throws {Error}
	 * Thrown when rootModelPath does not resolve to an array, indicating
	 * a configuration error in the layout definition.
	 * @memberof CustomRepeaterComponent
	 */
	private resolveRootArray(): any[]
	{
		const path: string =
			this.field.props.rootModelPath as string;
		let rootField: FormlyFieldConfig =
			this.field;

		while (!AnyHelper.isNull(rootField.parent))
		{
			rootField = rootField.parent;
		}

		const resolved: unknown =
			path
				.split(AppConstants.characters.period)
				.reduce(
					(obj: any, key: string) =>
						obj?.[key],
					rootField.model);

		if (!isArray(resolved))
		{
			throw new Error(
				`rootModelPath '${path}' did not resolve to an array. `
					+ 'Verify the path exists on the root model.');
		}

		return resolved;
	}

	/**
	 * Returns the parent item's reference identifier. Reads from the
	 * model first, then falls back to the formControl value if the
	 * model has not yet been updated.
	 *
	 * @returns {string}
	 * The parent reference identifier.
	 * @memberof CustomRepeaterComponent
	 */
	private getParentResourceIdentifier(): string
	{
		const parentReferenceKey: string =
			(this.field.props.parentReferenceKey as string)
				?? AppConstants.commonProperties.resourceIdentifier;

		const modelValue: string =
			this.field.parent.model?.[parentReferenceKey];
		const formControlValue: string =
			this.field.parent.fieldGroup
				?.find(
					(field: FormlyFieldConfig) =>
						field.key === parentReferenceKey)
				?.formControl?.value;

		return modelValue ?? formControlValue;
	}

	/**
	 * Attaches a virtual getter for this field's key on the parent model.
	 * The getter filters the root array down to only the items belonging
	 * to this parent, so formly renders the correct subset. Skipped on
	 * subsequent calls once the property is already defined.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private bindRootModel(): void
	{
		const propertyKey: string =
			this.field.key as string;
		const parentModel: any =
			this.field.parent.model;

		if (this.isRootModelPropertyDefined(
			parentModel,
			propertyKey))
		{
			return;
		}

		this.defineRootModelGetter(
			parentModel,
			propertyKey);

		this.persistParentReferenceKey(
			parentModel);

		this.options.build(
			this.field.parent);
	}

	/**
	 * Returns true when the root model getter has already been defined
	 * for the given property key, preventing duplicate definitions.
	 *
	 * @param {any} parentModel
	 * The parent model to inspect.
	 * @param {string} propertyKey
	 * The property key to check.
	 * @returns {boolean}
	 * True when the property is already defined.
	 * @memberof CustomRepeaterComponent
	 */
	private isRootModelPropertyDefined(
		parentModel: any,
		propertyKey: string): boolean
	{
		return !AnyHelper.isNull(
			Object.getOwnPropertyDescriptor(
				parentModel,
				propertyKey));
	}

	/**
	 * Defines a virtual getter on the parent model for the field key.
	 * The getter returns only the root array items that belong to this
	 * parent, filtered by matching reference ID. The setter is a no-op
	 * because formly model assignments are ignored for root-bound repeaters.
	 *
	 * @param {any} parentModel
	 * The parent model to define the property on.
	 * @param {string} propertyKey
	 * The property key to define.
	 * @memberof CustomRepeaterComponent
	 */
	private defineRootModelGetter(
		parentModel: any,
		propertyKey: string): void
	{
		const capturedField: FormlyFieldConfig =
			this.field;
		const capturedParentModel: any =
			parentModel;

		Object.defineProperty(
			parentModel,
			propertyKey,
			{
				get: (): any[] =>
					this.getFilteredRootItems(
						capturedField,
						capturedParentModel),
				set:
					() =>
					{
						// Intentional no-op; formly model assignments are
						// ignored for root-bound repeaters.
					},
				enumerable: false,
				configurable: true
			});
	}

	/**
	 * Resolves the root array via rootModelPath and filters it to only
	 * the items belonging to the given parent, identified by matching
	 * the item's reference ID key to the parent's resource identifier.
	 *
	 * @param {FormlyFieldConfig} capturedField
	 * The field captured at the time the getter was defined.
	 * @param {any} capturedParentModel
	 * The parent model captured at the time the getter was defined.
	 * @returns {any[]}
	 * The filtered subset of root array items belonging to this parent.
	 * @memberof CustomRepeaterComponent
	 */
	private getFilteredRootItems(
		capturedField: FormlyFieldConfig,
		capturedParentModel: any): any[]
	{
		const referencePath: string =
			capturedField.props.rootModelPath as string;
		const parentReferenceIdKey: string =
			(capturedField.props.parentReferenceIdKey as string)
				?? AppConstants.commonProperties.parentReferenceId;
		const parentReferenceKey: string =
			(capturedField.props.parentReferenceKey as string)
				?? AppConstants.commonProperties.resourceIdentifier;

		let root: FormlyFieldConfig = capturedField;
		while (!AnyHelper.isNull(root.parent))
		{
			root = root.parent;
		}

		const rootArray: any[] =
			referencePath
				.split(AppConstants.characters.period)
				.reduce(
					(obj: any, key: string) =>
						obj?.[key],
					root.model);

		if (AnyHelper.isNull(rootArray))
		{
			return [];
		}

		const modelResourceIdentifier: string =
			capturedParentModel?.[parentReferenceKey];
		const formControlResourceIdentifier: string =
			capturedField.parent?.fieldGroup
				?.find(
					(field: FormlyFieldConfig) =>
						field.key === parentReferenceKey)
				?.formControl?.value;
		const parentResourceIdentifier: string =
			modelResourceIdentifier ?? formControlResourceIdentifier;

		if (AnyHelper.isNullOrWhitespace(parentResourceIdentifier))
		{
			return [];
		}

		return rootArray.filter(
			(item: any) =>
				item[parentReferenceIdKey] === parentResourceIdentifier);
	}

	/**
	 * Copies the parent reference identifier from the formControl into
	 * the model before rebuild, ensuring getParentResourceIdentifier
	 * can reliably read it from the model after options.build() recreates
	 * the component and its formControls.
	 *
	 * @param {any} parentModel
	 * The parent model to persist the reference key onto.
	 * @memberof CustomRepeaterComponent
	 */
	private persistParentReferenceKey(parentModel: any): void
	{
		const referenceKey: string =
			(this.field.props.parentReferenceKey as string)
				?? AppConstants.commonProperties.resourceIdentifier;

		if (AnyHelper.isNullOrWhitespace(
			parentModel[referenceKey] as string))
		{
			const referenceControlValue: string =
				this.field.parent?.fieldGroup
					?.find(
						(field: FormlyFieldConfig) =>
							field.key === referenceKey)
					?.formControl?.value as string;

			if (!AnyHelper.isNullOrWhitespace(referenceControlValue))
			{
				parentModel[referenceKey] = referenceControlValue;
			}
		}
	}

	/**
	 * Implements a limited set of the existing formly remove method that
	 * ensures compatibility with our any of repeaters.
	 *
	 * @param {number} itemIndex
	 * The array item index to be removed.
	 * @param {boolean} removeFromModel
	 * A value signifying whether or not this remove call should remove from
	 * the component level model property.
	 * @param {boolean} emitEvent
	 * Whether to emit change events on the FormArray. Defaults to true.
	 * Pass false when removing multiple items in a loop to prevent
	 * mid-loop interference from Formly change detection.
	 * @memberof CustomRepeaterComponent
	 */
	private removeFromArray(
		itemIndex: number,
		removeFromModel: boolean = true,
		emitEvent: boolean = true): void
	{
		if (removeFromModel === true)
		{
			this.model.splice(itemIndex, 1);
		}

		this.unregisterControl(
			this.field.fieldGroup[itemIndex],
			emitEvent);

		this.field.fieldGroup.splice(
			itemIndex,
			1);

		this.field.fieldGroup.forEach(
			(field: FormlyFieldConfig,
				key: string | number) =>
				field.key = `${key}`);
	}

	/**
	 * A shared set of logic that will un-register a control similar to the
	 * unregister on the field array type this component extends.
	 *
	 * @param {FormlyFieldConfigCache} field
	 * The formly field config that should be unregistered.
	 * @param {boolean} emitEvent
	 * A value signifying whether or not this event should be emitted when
	 * complete.
	 * @memberof CustomRepeaterComponent
	 */
	private unregisterControl(
		field: FormlyFieldConfigCache,
		emitEvent: boolean = false): void
	{
		if (AnyHelper.isNull(field) || AnyHelper.isNull(field.formControl))
		{
			return;
		}

		const control: any = field.formControl;
		const fieldIndex =
			control._fields
				? control._fields.indexOf(field)
				: -1;
		if (fieldIndex !== -1)
		{
			control._fields.splice(
				fieldIndex,
				1);
		}

		const formArray =
			control.parent as UntypedFormArray;

		if (AnyHelper.isNull(formArray)
			|| AnyHelper.isNull(formArray.controls.findIndex))
		{
			return;
		}

		const key =
			formArray.controls.findIndex(
				(formControl: AbstractControl) =>
					formControl === control);

		if (key !== -1)
		{
			if (AnyHelper.isNull(formArray.removeAt))
			{
				return;
			}

			formArray.removeAt(
				key,
				{ emitEvent });
		}

		control.setParent(null);
	}

	/**
	 * This method will run a validation check on the sent item index in this
	 * displayed repeater.
	 *
	 * @param {number} itemIndex
	 * The item index that will need to run an explicit validation check.
	 * @memberof CustomRepeaterComponent
	 */
	private updateViewValidations(
		itemIndex: number): void
	{
		from([0, .25])
			.pipe(
				map((intervalTime: number) =>
					interval(intervalTime * 1000)
						.pipe(take(1))),
				concatAll())
			.subscribe(
				() =>
				{
					if (AnyHelper.isNull(this.field.fieldGroup[itemIndex])
						|| this.field.fieldGroup[itemIndex]
							.props.virtualDisplay === false)
					{
						return;
					}

					this.updateNestedRepeaterContext(
						this.field.fieldGroup[itemIndex]);

					FormlyHelper.fireViewCheck(
						<FormlyFieldConfig[]>
						[
							this.field.fieldGroup[itemIndex]
						]);
					this.fireChanges();
				});
	}

	/**
	 * This method will run the logic required to make sure that a newly added
	 * item will hold the same properties as the sent initial model. This value
	 * exists in the fields template options under the property name of
	 * 'initialItemValue'.
	 *
	 * @param {number} newItemIndex
	 * The new item index that needs to be aligned with the initial model.
	 * @param {any} initialModel
	 * The initial model value that we need to set.
	 * @memberof CustomRepeaterComponent
	 */
	private alignInitialItemValues(
		newItemIndex: number,
		initialModel: any): void
	{
		if (AnyHelper.isNull(this.model[newItemIndex]))
		{
			return;
		}

		this.model[newItemIndex] =
			{
				...this.model[newItemIndex] || {},
				...initialModel
			};
	}

	/**
	 * Determines if there are any existing repeater fields displayed
	 * and clean performs a clean model action to each.
	 *
	 * @param {FormlyFieldConfig} field
	 * The repeater field to look for existing related repeater items to clean.
	 * @memberof CustomRepeaterComponent
	 */
	private cleanExistingModelFields(field: FormlyFieldConfig): void
	{
		const existingFieldItems: FormlyFieldConfig[] =
			field?.fieldGroup.filter(
				(item: FormlyFieldConfig) =>
					item.props.virtualDisplay === true);

		if (AnyHelper.isNull(existingFieldItems)
			|| existingFieldItems.length === 0
			|| AnyHelper.isNull(this.model))
		{
			return;
		}

		existingFieldItems.forEach(
			(existingFieldItem: FormlyFieldConfig) =>
			{
				this.cleanModelField(
					this.model[<number>existingFieldItem.key],
					<number>existingFieldItem.key,
					existingFieldItem.fieldGroup);
			});
	}

	/**
	 * Performs a model clean for the repeater field based
	 * on the existing formGroup schema properties by removing
	 * from the model array those data items that do not match.
	 *
	 * @param {object} model
	 * The given model to check.
	 * @param {object} fieldIndexInModel
	 * The index of the model that relates
	 * with the field repeater item.
	 * @param {FormlyFieldConfig[]} fieldGroup
	 * The to check if any field matches the key path in the model.
	 * This will unset/remove the build model item in case
	 * the key path is found as part of the fieldFormGroup.
	 * @param {string} parentKey
	 * In case of nested object this will add the key predecesor.
	 * @memberof CustomRepeaterComponent
	 */
	private cleanModelField(
		model: object,
		fieldIndexInModel: number,
		fieldGroup: FormlyFieldConfig[],
		parentKey: string = AppConstants.empty)
	{
		if (AnyHelper.isNull(model))
		{
			return;
		}

		each(Object.keys(model),
			(item: any) =>
			{
				const nestedObjectKey: string =
					!AnyHelper.isNullOrWhitespace(parentKey)
					 	? `${parentKey}.${item}`
						: item;

				const dataKey: string =
					!isNaN(parseInt(
						item,
						AppConstants.parseRadix))
						? `${parentKey}[${item}]`
						: nestedObjectKey;

				if (isObject(model[item]))
				{
					if (isArray(model[item]))
					{
						if (!fieldGroup.some(
							(field: FormlyFieldConfig) =>
								field.key?.toString().indexOf(dataKey) >= 0
									|| field.props.complexDataStorage !== true))
						{
							unset(
								this.model[fieldIndexInModel],
								dataKey);

							return;
						}
					}

					this.cleanModelField(
						model[item],
						fieldIndexInModel,
						fieldGroup,
						dataKey);
				}
			});
	}

	/**
	 * Performs a form controls initialization for the repeater.
	 * This makes sure that form controls are synch within the model.
	 * This fixes an internal error when using the same model key value on a
	 * repeater but also on plain formly layout controls.
	 *
	 * @memberof CustomRepeaterComponent
	 */
	private InitializeFormControls(): void
	{
		if (this.field.formControl.controls != null)
		{
			this.field.formControl.controls = [];
		}
	}
}